import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AdminClientCreateInputSchema,
  type AdminClientCreateInput,
  ENTITY_TYPES,
  COUNTRY_CODES,
  SECTORS,
  type CountryCode,
  type EntityType,
  type Sector,
} from '@shared';
import { apiGet, apiPost, ApiError } from '../../lib/api';
import { applyFieldErrors } from '../../lib/form-errors';
import { useLegal } from '../../lib/legal-context';

// Human-readable labels for the enum dropdowns. Mirrors the public sign-up
// wizard's wording (src/client/pages/portal/SignUp.tsx) so the operator-side
// vocabulary stays consistent with the client-facing surface.
const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  Ltd: 'Limited Company',
  PLC: 'PLC',
  LLP: 'LLP',
  'FZ-LLC': 'Free Zone LLC',
  Other: 'Other',
};

const COUNTRY_LABELS: Record<CountryCode, string> = {
  UK: 'United Kingdom',
  'AE-MAIN': 'UAE (Mainland)',
  'AE-FZ': 'UAE (Free Zone)',
  IN: 'India',
  SG: 'Singapore',
  BVI: 'BVI',
  KY: 'Cayman',
  EU: 'EU',
  SEA: 'South-East Asia',
  NA: 'North America',
  OTHER: 'Other',
};

const SECTOR_LABELS: Record<Sector, string> = {
  'Shipping/Logistics': 'Shipping / Logistics',
  'Energy/Renewables': 'Energy / Renewables',
  'Real Estate': 'Real Estate',
  Infrastructure: 'Infrastructure',
  Commodities: 'Commodities',
  'PE/VC': 'Private Equity / VC',
  'Family Office': 'Family Office',
  Technology: 'Technology',
  Other: 'Other',
};

interface LeadOption {
  id: string;
  name: string;
  role: string;
  initials: string;
}

export function AdminClientCreate() {
  const legal = useLegal();
  const nav = useNavigate();
  const [leads, setLeads] = useState<LeadOption[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<AdminClientCreateInput>({
    resolver: zodResolver(AdminClientCreateInputSchema),
    defaultValues: {
      legal_name: '',
      trading_name: '',
      entity_type: 'Ltd',
      registration_number: '',
      country: 'UK',
      sector: 'Other',
      vertical: '',
      registered_address: '',
      website: '',
      lead_staff_id: '',
      body_corporate_confirmed: false as unknown as true,
      sophisticated_self_cert: false as unknown as true,
      not_advice_acknowledged: false as unknown as true,
      terms_accepted: false as unknown as true,
    },
  });

  useEffect(() => {
    apiGet<{ leads: LeadOption[] }>('/api/users/leads')
      .then((r) => setLeads(r.leads))
      .catch(() => setLeads([]));
  }, []);

  const onSubmit = async (data: AdminClientCreateInput) => {
    setSubmitError(null);
    // Normalise empty-string optionals to undefined so the server doesn't see
    // a `website: ''` and try to validate it as a URL.
    const payload: Record<string, unknown> = { ...data };
    for (const k of ['trading_name', 'vertical', 'registered_address', 'website', 'lead_staff_id'] as const) {
      if (payload[k] === '' || payload[k] === null) delete payload[k];
    }
    try {
      const r = await apiPost<{ client: { id: string } }>('/api/clients', payload);
      nav(`/admin/clients/${r.client.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: { code?: string; message?: string; detail?: unknown } } | null;
        const code = body?.error?.code;
        if (err.status === 400 && code === 'validation_failed') {
          const handled = applyFieldErrors(body?.error?.detail, setError);
          if (!handled) setSubmitError(err.serverMessage);
          return;
        }
        if (err.status === 422 && code === 'invalid_lead_staff') {
          setError('lead_staff_id', { type: 'server', message: err.serverMessage });
          return;
        }
        setSubmitError(err.serverMessage);
      } else {
        setSubmitError('Failed to create client. Please try again.');
      }
    }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
        <div>
          <h1 className="portal-h1">New client</h1>
          <p className="portal-sub">
            Create a body-corporate record. The four FCA-perimeter gates below must be
            confirmed before the record is created — this stamps you as having vouched
            for eligibility on the client's behalf.
          </p>
        </div>
        <Link to="/admin/clients" className="btn ghost">← All clients</Link>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="client-create"
        noValidate
        style={{ marginTop: 24 }}
      >
        <section className="card client-create-section">
          <h3 className="client-create-h">Identity</h3>
          <p className="client-create-help">Legal name and registry details. Used on engagement letters and invoices.</p>

          <div className="field">
            <label htmlFor="legal_name">Legal company name<span className="req">*</span></label>
            <input
              id="legal_name"
              type="text"
              autoComplete="organization"
              placeholder="e.g. Helios Marine Ltd"
              aria-invalid={errors.legal_name ? 'true' : 'false'}
              {...register('legal_name')}
            />
            {errors.legal_name && <div className="client-create-err">{errors.legal_name.message}</div>}
          </div>

          <div className="field">
            <label htmlFor="trading_name">Trading name</label>
            <input
              id="trading_name"
              type="text"
              placeholder="Optional — only if different from legal name"
              {...register('trading_name')}
            />
            {errors.trading_name && <div className="client-create-err">{errors.trading_name.message}</div>}
          </div>

          <div className="client-create-grid">
            <div className="field">
              <label htmlFor="entity_type">Entity type<span className="req">*</span></label>
              <select id="entity_type" {...register('entity_type')}>
                {ENTITY_TYPES.map((t) => (
                  <option key={t} value={t}>{ENTITY_TYPE_LABELS[t]}</option>
                ))}
              </select>
              {errors.entity_type && <div className="client-create-err">{errors.entity_type.message}</div>}
            </div>

            <div className="field">
              <label htmlFor="registration_number">Company / registration no.<span className="req">*</span></label>
              <input
                id="registration_number"
                type="text"
                placeholder="09812334"
                aria-invalid={errors.registration_number ? 'true' : 'false'}
                {...register('registration_number')}
              />
              {errors.registration_number && <div className="client-create-err">{errors.registration_number.message}</div>}
            </div>
          </div>

          <div className="client-create-grid">
            <div className="field">
              <label htmlFor="country">Country of incorporation<span className="req">*</span></label>
              <select id="country" {...register('country')}>
                {COUNTRY_CODES.map((c) => (
                  <option key={c} value={c}>{COUNTRY_LABELS[c]}</option>
                ))}
              </select>
              {errors.country && <div className="client-create-err">{errors.country.message}</div>}
            </div>

            <div className="field">
              <label htmlFor="sector">Sector<span className="req">*</span></label>
              <select id="sector" {...register('sector')}>
                {SECTORS.map((s) => (
                  <option key={s} value={s}>{SECTOR_LABELS[s]}</option>
                ))}
              </select>
              {errors.sector && <div className="client-create-err">{errors.sector.message}</div>}
            </div>
          </div>

          <div className="field">
            <label htmlFor="vertical">Vertical / sub-sector</label>
            <input
              id="vertical"
              type="text"
              placeholder="Optional — narrows the sector (e.g. Bulk shipping)"
              {...register('vertical')}
            />
            {errors.vertical && <div className="client-create-err">{errors.vertical.message}</div>}
          </div>

          <div className="field">
            <label htmlFor="registered_address">Registered address</label>
            <textarea
              id="registered_address"
              rows={3}
              placeholder="Optional — appears on invoices and engagement letters"
              {...register('registered_address')}
            />
            {errors.registered_address && <div className="client-create-err">{errors.registered_address.message}</div>}
          </div>

          <div className="field">
            <label htmlFor="website">Website</label>
            <input
              id="website"
              type="url"
              placeholder="https://"
              autoComplete="url"
              aria-invalid={errors.website ? 'true' : 'false'}
              {...register('website')}
            />
            {errors.website && <div className="client-create-err">{errors.website.message}</div>}
          </div>
        </section>

        <section className="card client-create-section">
          <h3 className="client-create-h">Engagement lead</h3>
          <p className="client-create-help">Assign now or leave blank — the lead can be set later from the client detail page.</p>

          <div className="field">
            <label htmlFor="lead_staff_id">Lead operator</label>
            <select id="lead_staff_id" {...register('lead_staff_id')} aria-invalid={errors.lead_staff_id ? 'true' : 'false'}>
              <option value="">Unassigned</option>
              {leads.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} · {l.role}
                </option>
              ))}
            </select>
            {errors.lead_staff_id && <div className="client-create-err">{errors.lead_staff_id.message}</div>}
          </div>
        </section>

        <section className="card client-create-section">
          <h3 className="client-create-h">FCA-perimeter eligibility</h3>
          <p className="client-create-help">
            Confirmation of eligibility is required before the record is created. You are vouching on the
            client's behalf — the client's authorised contact will re-affirm via the portal at their first sign-in.
          </p>

          {(() => {
            // Hidden RHF registrations keep the four flags in the form state so
            // Zod validation + submission still receive them. The single master
            // checkbox flips all four atomically via setValue.
            const bodyCorp = watch('body_corporate_confirmed') === true;
            const sophisticated = watch('sophisticated_self_cert') === true;
            const notAdvice = watch('not_advice_acknowledged') === true;
            const terms = watch('terms_accepted') === true;
            const allFour = bodyCorp && sophisticated && notAdvice && terms;

            const toggleAll = () => {
              const next = !allFour;
              setValue('body_corporate_confirmed', next as true, { shouldValidate: true, shouldDirty: true });
              setValue('sophisticated_self_cert',  next as true, { shouldValidate: true, shouldDirty: true });
              setValue('not_advice_acknowledged',  next as true, { shouldValidate: true, shouldDirty: true });
              setValue('terms_accepted',           next as true, { shouldValidate: true, shouldDirty: true });
            };

            const firstError =
              errors.body_corporate_confirmed?.message
              ?? errors.sophisticated_self_cert?.message
              ?? errors.not_advice_acknowledged?.message
              ?? errors.terms_accepted?.message;

            return (
              <>
                <input type="hidden" {...register('body_corporate_confirmed')} />
                <input type="hidden" {...register('sophisticated_self_cert')} />
                <input type="hidden" {...register('not_advice_acknowledged')} />
                <input type="hidden" {...register('terms_accepted')} />

                <ul style={{
                  margin: '14px 0 0',
                  paddingLeft: 22,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  fontSize: 13.5,
                  color: 'var(--fg-soft)',
                  lineHeight: 1.55,
                }}>
                  <li>{splitFirstSentence(legal.contact_eligibility_body_corp)}</li>
                  <li>{splitFirstSentence(legal.contact_eligibility_sophisticated)}</li>
                  <li>{splitFirstSentence(legal.contact_eligibility_not_advice)}</li>
                  <li>
                    <strong style={{ color: 'var(--fg)', fontWeight: 600 }}>
                      Terms of Business.
                    </strong>{' '}
                    The company accepts the Omega Trade and Finance Ltd{' '}
                    <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Business</Link>
                    {' '}and the{' '}
                    <Link to="/regulatory" target="_blank" rel="noopener noreferrer">Regulatory Position</Link>.
                  </li>
                </ul>

                <label
                  className={`checkbox ${allFour ? 'on' : ''}`}
                  onClick={toggleAll}
                  style={{ marginTop: 14 }}
                >
                  <span className="box" />
                  <span>I confirm all the eligibility criteria are met on behalf of this client.</span>
                </label>

                {firstError && (
                  <div className="client-create-err" style={{ marginTop: 8 }}>
                    {firstError}
                  </div>
                )}
              </>
            );
          })()}
        </section>

        {submitError && (
          <div className="client-create-banner" role="alert">
            {submitError}
          </div>
        )}

        <div className="client-create-actions">
          <Link to="/admin/clients" className="btn ghost">Cancel</Link>
          <button type="submit" className="btn accent" disabled={isSubmitting}>
            {isSubmitting ? 'Creating…' : 'Create client'}
          </button>
        </div>
      </form>
    </>
  );
}

// Render the first sentence of an eligibility statement in a heavier weight so
// the headline claim stands out from the supporting detail. Mirrors the helper
// in Contact.tsx so the public and operator surfaces look consistent.
function splitFirstSentence(text: string) {
  const m = text.match(/^([^.]+\.)\s*(.*)$/);
  if (!m) return text;
  return (
    <>
      <strong style={{ color: 'var(--fg)', fontWeight: 600 }}>{m[1]}</strong>{' '}{m[2]}
    </>
  );
}
